import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { getTranslations } from './locales';
import {
  applyDocumentLocale,
  getVolumeLettersPath,
  getGuidePath,
  getHomePath,
  getInitialLocale,
} from './locale';
import { Locale, TranslationKeys } from './types';

type I18nContextValue = {
  locale: Locale;
  t: TranslationKeys;
  setLocale: (locale: Locale) => void;
};

const I18nContext = createContext<I18nContextValue | null>(null);

type I18nProviderProps = {
  children: React.ReactNode;
  initialLocale?: Locale;
};

export const I18nProvider: React.FC<I18nProviderProps> = ({ children, initialLocale }) => {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const initial = initialLocale ?? getInitialLocale();
    applyDocumentLocale(initial);
    return initial;
  });

  const setLocale = useCallback((next: Locale) => {
    applyDocumentLocale(next);
    setLocaleState(next);

    const { pathname } = window.location;
    const onHome =
      pathname === '/' ||
      pathname === '/index.html' ||
      pathname === '/es.html' ||
      pathname.endsWith('/es.html');
    const onGuide =
      pathname.endsWith('/lego-name-guide.html') || pathname.endsWith('/guia-nombre-lego.html');
    const onVolumeLetters =
      pathname.endsWith('/volume-letters.html') || pathname.endsWith('/letras-volumen.html');

    if (onHome) {
      const target = getHomePath(next);
      if (pathname !== target && pathname !== `${target}index.html`) {
        window.location.href = target;
      }
      return;
    }

    if (onGuide) {
      const target = getGuidePath(next);
      if (!pathname.endsWith(target)) {
        window.location.href = target;
      }
      return;
    }

    if (onVolumeLetters) {
      const target = getVolumeLettersPath(next);
      if (!pathname.endsWith(target)) {
        window.location.href = target;
      }
    }
  }, []);

  const value = useMemo(
    () => ({
      locale,
      t: getTranslations(locale),
      setLocale,
    }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within I18nProvider');
  }
  return ctx;
}
